import { TrashIcon } from "@heroicons/react/outline";
import { useContext, useMemo } from "react";

import { DatasetEditorContext } from "@/components/DatasetEditor";
import { Button } from "@/components/ui/button";
import { useDeleteEntity } from "@/components/UnifiedRegistry/Dialogs/hooks/useDeleteEntity";
import { Entity, EntityType } from "@/queries";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useRbac } from "@/providers/RbacProvider";

const ArchiveButton = ({ onDelete }: { onDelete: () => void }) => {
  const context = useContext(DatasetEditorContext);
  const { deleteEntity } = useDeleteEntity();
  const { isEditDisabled } = useRbac();
  const cannotEditDataset = isEditDisabled(EntityType.DATASET);

  const isArchived = useMemo(() => !!context?.datasetGroup?.is_deleted, [context?.datasetGroup]);

  if (!context?.datasetGroup || context?.datasetGroup?.is_deleted) {
    return (
      <div className="flex flex-row items-center">
        <div className="mr-2 h-1 w-1 rounded-full bg-gray-300" />
        <span className="self-center text-sm text-gray-400">Archived</span>
      </div>
    );
  }

  const isDisplayed = !isArchived && !context?.isLoading && !context?.isDraft;

  const handleDelete = () => {
    const entity = {
      id: context?.datasetGroup?.id,
      name: context?.datasetGroup?.name,
      type: EntityType.DATASET,
      path: [],
    };
    deleteEntity(entity as Entity<EntityType>, onDelete, true);
  };

  return (
    (isDisplayed && (
      <BasicTooltip
        content={
          cannotEditDataset ? "You don't have permission to archive this dataset" : undefined
        }
        shouldShow={cannotEditDataset}
      >
        <span>
          <Button
            onClick={handleDelete}
            variant="destructiveOutline"
            size="sm"
            className="inline-flex items-center"
            disabled={cannotEditDataset}
          >
            <TrashIcon className="h-4 w-auto pr-1" aria-hidden="true" />
            Archive
          </Button>
        </span>
      </BasicTooltip>
    )) ||
    null
  );
};

export default ArchiveButton;
